// TanStack Query hooks for the S2 communities API (docs/api/communities.md). Query keys follow
// ARCHITECTURE 15 and the API notes: ["community", slug, ...], ["discover", params], ["me", "communities"].
import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api, withQuery, type Page } from '@/lib/api'
import { ME_KEY, type Me } from '@/lib/auth'
import { communityKey } from '@/lib/community'
import type { ExitAnswer } from '@/lib/exitFeedback'
import type {
  About,
  AuditEntry,
  Category,
  CategoryInput,
  CommunityFull,
  CreateCommunityInput,
  DiscoverCard,
  DiscoverCategory,
  DiscoverParams,
  GettingStarted,
  HomeCommunity,
  Invite,
  InviteBatch,
  InviteLanding,
  InviteSendResult,
  JoinAnswer,
  JoinResult,
  Listing,
  MembershipPrefs,
  MyCommunity,
  Plugin,
  Review,
  ReviewEligibility,
  ReviewSummary,
  SlugCheck,
  WaitlistEntry,
  WaitlistJoin,
  WaitlistMe,
  Welcome,
} from './types'

const enc = encodeURIComponent
const base = (slug: string) => `/api/communities/${enc(slug)}`
const low = (slug: string) => slug.toLowerCase()

export const communityKeys = {
  shell: (slug: string) => communityKey(slug),
  about: (slug: string, extra?: { ref?: string | null }) => ['community', low(slug), 'about', extra ?? {}] as const,
  aboutAll: (slug: string) => ['community', low(slug), 'about'] as const,
  categories: (slug: string) => ['community', low(slug), 'categories'] as const,
  plugins: (slug: string) => ['community', low(slug), 'plugins'] as const,
  invites: (slug: string, params?: object) => ['community', low(slug), 'invites', params ?? {}] as const,
  invitesAll: (slug: string) => ['community', low(slug), 'invites'] as const,
  batch: (slug: string, id: string) => ['community', low(slug), 'invite-batch', id] as const,
  reviews: (slug: string, params?: object) => ['community', low(slug), 'reviews', params ?? {}] as const,
  reviewsAll: (slug: string) => ['community', low(slug), 'reviews'] as const,
  eligibility: (slug: string) => ['community', low(slug), 'reviews', 'eligibility'] as const,
  welcome: (slug: string) => ['community', low(slug), 'welcome'] as const,
  gettingStarted: (slug: string) => ['community', low(slug), 'getting-started'] as const,
  listing: (slug: string) => ['community', low(slug), 'listing'] as const,
  audit: (slug: string, params?: object) => ['community', low(slug), 'audit', params ?? {}] as const,
  waitlist: (slug: string, page: number) => ['community', low(slug), 'waitlist', page] as const,
  waitlistMe: (slug: string) => ['community', low(slug), 'waitlist-me'] as const,
  inviteLanding: (token: string) => ['invite', token] as const,
  discover: (params: DiscoverParams) => ['discover', params] as const,
  discoverCategories: ['discover', 'categories'] as const,
  featured: (limit: number) => ['discover', 'featured', limit] as const,
  home: ['discover', 'home'] as const,
  slug: (slug: string, communityId?: string) => ['slug-availability', slug, communityId ?? null] as const,
  mine: ['me', 'communities'] as const,
}

/** After join, leave, membership prefs or order changes (API notes). */
export function invalidateMembership(qc: QueryClient, slug?: string) {
  void qc.invalidateQueries({ queryKey: ME_KEY })
  if (slug) void qc.invalidateQueries({ queryKey: communityKeys.shell(slug) })
}

// ---------- Community shell, about, create, settings ----------

/** The full shell payload (same cache entry as the foundation useCommunity). */
export function useCommunityFull(slug: string | undefined) {
  return useQuery({
    queryKey: communityKeys.shell(slug ?? ''),
    queryFn: () => api.get<CommunityFull>(base(slug ?? '')),
    enabled: !!slug,
    staleTime: 60_000,
  })
}

export function useAbout(slug: string, ref?: string | null) {
  return useQuery({
    queryKey: communityKeys.about(slug, ref ? { ref } : undefined),
    queryFn: () => api.get<About>(withQuery(`${base(slug)}/about`, { ref: ref || undefined })),
    enabled: !!slug,
    staleTime: 30_000,
  })
}

export function useSlugAvailability(slug: string, opts: { communityId?: string; enabled?: boolean } = {}) {
  const s = slug.trim().toLowerCase()
  return useQuery({
    queryKey: communityKeys.slug(s, opts.communityId),
    queryFn: ({ signal }) => api.get<SlugCheck>(withQuery('/api/slugs/availability', { slug: s, communityId: opts.communityId }), { signal }),
    enabled: (opts.enabled ?? true) && s.length > 0,
    staleTime: 15_000,
    retry: false,
  })
}

export function useCreateCommunity() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateCommunityInput) => api.post<CommunityFull>('/api/communities', input),
    onSuccess: (community) => {
      qc.setQueryData(communityKeys.shell(community.slug), community)
      void qc.invalidateQueries({ queryKey: ME_KEY })
      void qc.invalidateQueries({ queryKey: communityKeys.mine })
    },
  })
}

export type CommunityPatch = Record<string, unknown>

/** PATCH general settings; a slug change moves the cache to the new slug. */
export function useUpdateCommunity(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: CommunityPatch) => api.patch<CommunityFull>(base(slug), patch),
    onSuccess: (community) => {
      qc.setQueryData(communityKeys.shell(community.slug), community)
      void qc.invalidateQueries({ queryKey: communityKeys.shell(community.slug) })
      // FAS-7: after a rename the old address answers 404 (redirectSlug), and the settings page moves to the new one
      // right away: mark the old address's queries stale without asking again (no 404s for the shell, About, steps).
      if (low(community.slug) !== low(slug)) void qc.invalidateQueries({ queryKey: communityKeys.shell(slug), refetchType: 'none' })
      void qc.invalidateQueries({ queryKey: ME_KEY })
    },
  })
}

export function useGettingStarted(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: communityKeys.gettingStarted(slug),
    queryFn: () => api.get<GettingStarted>(`${base(slug)}/getting-started`),
    enabled: enabled && !!slug,
    staleTime: 30_000,
  })
}

type StatusOut = { ok: boolean; status: string; slug: string }

/** Archive, unarchive, delete, transfer and doors. `run` lets the caller wrap the call in a re auth gate. */
export function useLifecycle(slug: string) {
  const qc = useQueryClient()
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: communityKeys.shell(slug) })
    void qc.invalidateQueries({ queryKey: ME_KEY })
  }
  const archive = useMutation({ mutationFn: () => api.post<StatusOut>(`${base(slug)}/archive`), onSuccess: refresh })
  const unarchive = useMutation({ mutationFn: () => api.post<StatusOut>(`${base(slug)}/unarchive`), onSuccess: refresh })
  const remove = useMutation({
    mutationFn: (fn: () => Promise<StatusOut>) => fn(),
    onSuccess: () => {
      qc.removeQueries({ queryKey: communityKeys.shell(slug) })
      // Drop it from the session at once: "/" must not send the owner back to it while /me refetches.
      qc.setQueryData<Me | null>(ME_KEY, (m) => (m ? { ...m, communities: m.communities.filter((c) => c.slug.toLowerCase() !== slug.toLowerCase()) } : m))
      void qc.invalidateQueries({ queryKey: ME_KEY })
    },
  })
  // No onSuccess refresh: the caller leaves settings first, then calls refresh(), so the owner only
  // section guard never sees the new admin role while the Danger zone is still open (Q-COMM-35).
  const transfer = useMutation({ mutationFn: (fn: () => Promise<StatusOut>) => fn() })
  const doors = useMutation({ mutationFn: (fn: () => Promise<CommunityFull>) => fn(), onSuccess: refresh })
  return {
    archive,
    unarchive,
    remove,
    transfer,
    doors,
    refresh,
    calls: {
      remove: (confirmName: string) => () => api.del<StatusOut>(base(slug), { confirmName }),
      transfer: (userId: string) => () => api.post<StatusOut>(`${base(slug)}/transfer`, { userId }),
      doors: (closed: boolean) => () => api.post<CommunityFull>(`${base(slug)}/doors`, { closed }),
    },
  }
}

export function useJoinWaitlist(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (email?: string) => api.post<WaitlistJoin>(`${base(slug)}/waitlist`, email ? { email } : {}),
    onSuccess: (res) => {
      // JOIN-019 cohorts: the caller's own place in line comes back only for their verified account address
      if (res.position != null || res.invited) {
        qc.setQueryData<WaitlistMe>(communityKeys.waitlistMe(slug), { onWaitlist: true, position: res.position, spotsLeft: res.spotsLeft ?? null, invited: !!res.invited })
      }
    },
  })
}

/** paid-admin-7: an admin takes someone off the waitlist (never a person who already joined). */
export function useRemoveWaitlistEntry(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (entryId: string) => api.del<void>(`${base(slug)}/waitlist/${encodeURIComponent(entryId)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['community', low(slug), 'waitlist'] }),
  })
}

/** The logged in viewer's own place in line (JOIN-019 cohorts). */
export function useMyWaitlist(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: communityKeys.waitlistMe(slug),
    queryFn: () => api.get<WaitlistMe>(`${base(slug)}/waitlist/me`),
    enabled: enabled && !!slug,
    staleTime: 60_000,
    retry: false,
  })
}

/** GET /waitlist: `total` counts every entry (the pages), `waiting` leaves out the people who joined (paid-admin-7). */
export type WaitlistPage = Page<WaitlistEntry> & { waiting?: number }

export function useWaitlist(slug: string, page: number, enabled = true) {
  return useQuery({
    queryKey: communityKeys.waitlist(slug, page),
    queryFn: () => api.get<WaitlistPage>(withQuery(`${base(slug)}/waitlist`, { page })),
    enabled,
    placeholderData: keepPreviousData,
  })
}

export function useListing(slug: string, enabled = true) {
  return useQuery({ queryKey: communityKeys.listing(slug), queryFn: () => api.get<Listing>(`${base(slug)}/listing`), enabled })
}

export function useSetListing(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (listed: boolean) => api.post<Listing>(`${base(slug)}/listing`, { listed }),
    onSuccess: (listing) => {
      qc.setQueryData(communityKeys.listing(slug), listing)
      void qc.invalidateQueries({ queryKey: communityKeys.shell(slug) })
    },
  })
}

export function useAudit(slug: string, params: { page: number; group?: string }) {
  return useQuery({
    queryKey: communityKeys.audit(slug, params),
    queryFn: () => api.get<Page<AuditEntry>>(withQuery(`${base(slug)}/audit`, { page: params.page, group: params.group || undefined })),
    placeholderData: keepPreviousData,
  })
}

// ---------- Joining and my membership ----------

export type JoinInput = {
  answers?: JoinAnswer[]
  ref?: string | null
  inviteToken?: string | null
  source?: 'invite' | 'affiliate' | 'discover' | 'external' | 'direct'
  /** UTM tags from the landing URL (ANLY-010). */
  utm?: Record<string, string> | null
  /** Paid or freemium heading to the plan picker: "paid" stashes the answers and the campaign (200 checkout). */
  plan?: 'free' | 'paid'
}

export function useJoin(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: JoinInput) =>
      api.post<JoinResult>(`${base(slug)}/join`, {
        answers: input.answers ?? [],
        ref: input.ref || null,
        inviteToken: input.inviteToken || null,
        source: input.source,
        utm: input.utm ?? null,
        plan: input.plan,
      }),
    onSuccess: (res) => {
      if (res.status === 'checkout') return // nothing changed yet: the plan picker checks out next
      invalidateMembership(qc, slug)
      void qc.invalidateQueries({ queryKey: communityKeys.mine })
    },
  })
}

/** POST /leave with the optional "Why are you leaving?" answer (lib/exitFeedback). */
export function useLeave(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (answer?: ExitAnswer) => api.post<{ ok: boolean }>(`${base(slug)}/leave`, answer ?? {}),
    onSuccess: () => {
      invalidateMembership(qc, slug)
      void qc.invalidateQueries({ queryKey: communityKeys.mine })
    },
  })
}

export function useWithdrawRequest(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.del(`${base(slug)}/join-request`),
    onSuccess: () => {
      invalidateMembership(qc, slug)
      void qc.invalidateQueries({ queryKey: communityKeys.mine })
    },
  })
}

/** PATCH my membership prefs; optimistic on the ["me", "communities"] list. */
export function useUpdateMembership(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (prefs: MembershipPrefs) => api.patch<Record<string, unknown>>(`${base(slug)}/membership`, prefs),
    onMutate: async (prefs) => {
      await qc.cancelQueries({ queryKey: communityKeys.mine })
      const prev = qc.getQueryData<MyCommunity[]>(communityKeys.mine)
      if (prev) {
        qc.setQueryData<MyCommunity[]>(
          communityKeys.mine,
          prev.map((c) => (low(c.slug) === low(slug) ? applyPrefs(c, prefs) : c)),
        )
      }
      return { prev }
    },
    onError: (_e, _p, ctx) => {
      if (ctx?.prev) qc.setQueryData(communityKeys.mine, ctx.prev)
    },
    onSettled: (_r, _e, prefs) => {
      invalidateMembership(qc, slug)
      void qc.invalidateQueries({ queryKey: communityKeys.mine })
      // ACCT-025: the S1 profile lists memberships from GET /users/{handle} (docs/requests/S1-web.md item 4)
      if (prefs && 'showOnProfile' in prefs) void qc.invalidateQueries({ queryKey: ['user'] })
    },
  })
}

/** Pinning unhides and hiding unpins (SHELL-017). */
export function applyPrefs(c: MyCommunity, prefs: MembershipPrefs): MyCommunity {
  const next = { ...c }
  if (prefs.pinned !== undefined) {
    next.pinned = prefs.pinned
    if (prefs.pinned) next.hiddenInSwitcher = false
  }
  if (prefs.hiddenInSwitcher !== undefined) {
    next.hiddenInSwitcher = prefs.hiddenInSwitcher
    if (prefs.hiddenInSwitcher) next.pinned = false
  }
  if (prefs.chatEnabled !== undefined) next.chatEnabled = prefs.chatEnabled
  if (prefs.showOnProfile !== undefined) next.showOnProfile = prefs.showOnProfile
  return next
}

export function useWelcome(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: communityKeys.welcome(slug),
    queryFn: () => api.get<Welcome>(`${base(slug)}/welcome`),
    enabled: enabled && !!slug,
    staleTime: 5 * 60_000,
    retry: false,
  })
}

export function useMyCommunities(enabled = true) {
  return useQuery({ queryKey: communityKeys.mine, queryFn: () => api.get<MyCommunity[]>('/api/me/communities'), enabled })
}

export function useCommunityOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (communityIds: string[]) => api.put<{ ok: boolean }>('/api/me/community-order', { communityIds }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ME_KEY })
      void qc.invalidateQueries({ queryKey: communityKeys.mine })
    },
  })
}

// ---------- Categories and plugins ----------

export function useCategories(slug: string, enabled = true) {
  return useQuery({
    queryKey: communityKeys.categories(slug),
    queryFn: () => api.get<Category[]>(`${base(slug)}/categories`),
    enabled: enabled && !!slug,
  })
}

export function useCategoryMutations(slug: string) {
  const qc = useQueryClient()
  const done = () => {
    void qc.invalidateQueries({ queryKey: communityKeys.categories(slug) })
    // The feed chips (name, emoji, colour, default sort) come from the feed meta: refresh it too.
    void qc.invalidateQueries({ queryKey: ['feedMeta'] })
    // And this community's feed lists (features/feed feedKeys.all): a list keeps its key when the chip's default
    // sort changes (no picked sort), so the open feed (under the settings modal) would keep the old order; a delete
    // also moves posts to another category.
    void qc.invalidateQueries({ queryKey: ['posts'], predicate: (q) => String(q.queryKey[1]).toLowerCase() === low(slug) })
  }
  return {
    create: useMutation({ mutationFn: (input: CategoryInput) => api.post<Category>(`${base(slug)}/categories`, input), onSuccess: done }),
    update: useMutation({
      mutationFn: ({ id, input }: { id: string; input: CategoryInput }) => api.patch<Category>(`/api/categories/${enc(id)}`, input),
      onSuccess: done,
    }),
    reorder: useMutation({
      mutationFn: (categoryIds: string[]) => api.put<Category[]>(`${base(slug)}/categories/order`, { categoryIds }),
      onMutate: async (ids) => {
        await qc.cancelQueries({ queryKey: communityKeys.categories(slug) })
        const prev = qc.getQueryData<Category[]>(communityKeys.categories(slug))
        if (prev) {
          const byId = new Map(prev.map((c) => [c.id, c]))
          qc.setQueryData(
            communityKeys.categories(slug),
            ids.map((id, i) => ({ ...(byId.get(id) as Category), position: i })).filter((c) => c.id),
          )
        }
        return { prev }
      },
      onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(communityKeys.categories(slug), ctx.prev),
      onSettled: done,
    }),
    remove: useMutation({
      mutationFn: ({ id, moveTo }: { id: string; moveTo?: string }) => api.del(withQuery(`/api/categories/${enc(id)}`, { moveTo })),
      onSuccess: done,
    }),
  }
}

export function usePlugins(slug: string, enabled = true) {
  return useQuery({ queryKey: communityKeys.plugins(slug), queryFn: () => api.get<Plugin[]>(`${base(slug)}/plugins`), enabled })
}

export function useUpdatePlugin(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ key, ...body }: { key: string; enabled?: boolean; config?: Record<string, unknown>; resetSeen?: boolean }) =>
      api.put<Plugin>(`${base(slug)}/plugins/${enc(key)}`, body),
    onSuccess: (plugin) => {
      qc.setQueryData<Plugin[]>(communityKeys.plugins(slug), (old) => old?.map((p) => (p.key === plugin.key ? plugin : p)))
      void qc.invalidateQueries({ queryKey: communityKeys.shell(slug) })
      // FAS-1: the level gates also show in Gamification and on the level ladder (features/leaderboards boardKeys
      // gamification and levels): a stale Gamification form would save the old gate back.
      if (plugin.key === 'unlock_posting' || plugin.key === 'unlock_chat') {
        for (const root of ['gamification', 'levels']) {
          void qc.invalidateQueries({ queryKey: [root], predicate: (q) => String(q.queryKey[1]).toLowerCase() === low(slug) })
        }
      }
    },
  })
}

// ---------- Invites ----------

/** JOIN-010: what a CSV import would do before anything is sent. */
export type CsvPreview = { valid: number; invalid: number; alreadyMembers: number; toSend: number }

export type InviteListParams = { status?: string; q?: string; page?: number }

export function useInvites(slug: string, params: InviteListParams, enabled = true) {
  return useQuery({
    queryKey: communityKeys.invites(slug, params),
    queryFn: () => api.get<Page<Invite>>(withQuery(`${base(slug)}/invites`, params)),
    enabled,
    placeholderData: keepPreviousData,
  })
}

export function useInviteMutations(slug: string) {
  const qc = useQueryClient()
  const done = () => void qc.invalidateQueries({ queryKey: communityKeys.invitesAll(slug) })
  return {
    send: useMutation({
      mutationFn: (body: { emails: string[]; courseIds?: string[]; freeAccess?: boolean }) =>
        api.post<InviteSendResult>(`${base(slug)}/invites`, { courseIds: [], freeAccess: false, ...body }),
      onSuccess: done,
    }),
    previewCsv: useMutation({
      mutationFn: (uploadId: string) => api.post<CsvPreview>(`${base(slug)}/invites/import/preview`, { uploadId }),
    }),
    importCsv: useMutation({
      mutationFn: (body: { uploadId: string; courseIds?: string[]; freeAccess?: boolean }) =>
        api.post<InviteBatch>(`${base(slug)}/invites/import`, { courseIds: [], freeAccess: false, ...body }),
      onSuccess: done,
    }),
    resend: useMutation({ mutationFn: (id: string) => api.post<Invite>(`${base(slug)}/invites/${enc(id)}/resend`), onSuccess: done }),
    revoke: useMutation({ mutationFn: (id: string) => api.del(`${base(slug)}/invites/${enc(id)}`), onSuccess: done }),
  }
}

/** Polls a CSV import batch every 2 s until it is done or failed. */
export function useInviteBatch(slug: string, batchId: string | null) {
  const qc = useQueryClient()
  return useQuery({
    queryKey: communityKeys.batch(slug, batchId ?? ''),
    queryFn: async () => {
      const b = await api.get<InviteBatch>(`${base(slug)}/invite-batches/${enc(batchId ?? '')}`)
      // E2E-J5-03: the worker creates the invites, so the Invites list refreshes once the import ends.
      if (b.status === 'done' || b.status === 'failed') void qc.invalidateQueries({ queryKey: communityKeys.invitesAll(slug) })
      return b
    },
    enabled: !!batchId,
    refetchInterval: (q) => (q.state.data && (q.state.data.status === 'done' || q.state.data.status === 'failed') ? false : 2000),
  })
}

export function useInviteLanding(token: string | null) {
  return useQuery({
    queryKey: communityKeys.inviteLanding(token ?? ''),
    queryFn: () => api.get<InviteLanding>(`/api/invites/${enc(token ?? '')}`),
    enabled: !!token,
    retry: false,
  })
}

export function useAcceptInvite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (token: string) => api.post<{ status: string; slug: string; memberId: string }>(`/api/invites/${enc(token)}/accept`),
    onSuccess: (res) => {
      invalidateMembership(qc, res.slug)
      void qc.invalidateQueries({ queryKey: communityKeys.mine })
    },
  })
}

// ---------- Reviews ----------

export type ReviewPage = Page<Review> & { summary: ReviewSummary }

export function useReviews(slug: string, params: { sort: 'recent' | 'top'; page: number }) {
  return useQuery({
    queryKey: communityKeys.reviews(slug, params),
    queryFn: () => api.get<ReviewPage>(withQuery(`${base(slug)}/reviews`, { ...params, limit: 10 })),
    placeholderData: keepPreviousData,
  })
}

export function useReviewEligibility(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: communityKeys.eligibility(slug),
    queryFn: () => api.get<ReviewEligibility>(`${base(slug)}/reviews/eligibility`),
    enabled,
    retry: false,
  })
}

export function useReviewMutations(slug: string) {
  const qc = useQueryClient()
  const done = () => {
    void qc.invalidateQueries({ queryKey: communityKeys.reviewsAll(slug) })
    void qc.invalidateQueries({ queryKey: communityKeys.aboutAll(slug) })
  }
  return {
    create: useMutation({ mutationFn: (body: { rating: number; body: string }) => api.post<Review>(`${base(slug)}/reviews`, body), onSuccess: done }),
    update: useMutation({
      mutationFn: ({ id, ...body }: { id: string; rating?: number; body?: string }) => api.patch<Review>(`/api/reviews/${enc(id)}`, body),
      onSuccess: done,
    }),
    remove: useMutation({ mutationFn: (id: string) => api.del(`/api/reviews/${enc(id)}`), onSuccess: done }),
    report: useMutation({
      mutationFn: ({ id, reason, note }: { id: string; reason: string; note?: string }) =>
        api.post<{ ok: boolean; flagged: boolean }>(`/api/reviews/${enc(id)}/report`, { reason, note: note || undefined }),
      onSuccess: (res) => res.flagged && done(),
    }),
  }
}

// ---------- Discover ----------

export function useDiscover(params: DiscoverParams) {
  return useQuery({
    queryKey: communityKeys.discover(params),
    queryFn: () =>
      api.get<Page<DiscoverCard>>(
        withQuery('/api/discover', {
          q: params.q?.trim() || undefined,
          category: params.category && params.category !== 'all' ? params.category : undefined,
          price: params.price || undefined,
          language: params.language || undefined,
          sort: params.sort && params.sort !== 'trending' ? params.sort : undefined,
          page: params.page && params.page > 1 ? params.page : undefined,
          limit: 24,
        }),
      ),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  })
}

export function useDiscoverCategories() {
  return useQuery({
    queryKey: communityKeys.discoverCategories,
    queryFn: () => api.get<DiscoverCategory[]>('/api/discover/categories'),
    staleTime: 5 * 60_000,
  })
}

export function useFeatured(limit = 6, enabled = true) {
  return useQuery({
    queryKey: communityKeys.featured(limit),
    queryFn: () => api.get<DiscoverCard[]>(withQuery('/api/discover/featured', { limit })),
    enabled,
    staleTime: 5 * 60_000,
  })
}

/** REDESIGN 3: MG's published communities for the home page (listed in Discover or not). */
export function useHomeCommunities(enabled = true) {
  return useQuery({
    queryKey: communityKeys.home,
    queryFn: () => api.get<HomeCommunity[]>('/api/discover/home'),
    enabled,
    staleTime: 5 * 60_000,
  })
}

// ---------- Plans (S8 public endpoint, read only) ----------

export type PlanPrice = { id: string; amountCents: number; currency: string; interval: string | null } | null
export type PlanTier = {
  id: string
  name: string
  description: string | null
  benefits: string[]
  kind: 'free' | 'paid'
  position?: number
  trialDays: number
  prices: { month: PlanPrice; year: PlanPrice; oneTime: PlanPrice }
  savePct: number | null
}
export type Plans = {
  model: string
  currency: string
  tiers: PlanTier[]
  viewer: { tierId: string | null; memberStatus?: string | null; accessSource?: string | null } | null
  trialEligible?: boolean
}

export function usePlans(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: ['plans', slug.toLowerCase()],
    queryFn: () => api.get<Plans>(`/api/communities/${encodeURIComponent(slug)}/plans`),
    enabled,
    retry: false,
    staleTime: 60_000,
  })
}
